import React, { useEffect, useState, useCallback } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Chip, Toolbar, Select, MenuItem, FormControl, InputLabel, Snackbar, Alert } from '@mui/material';
import ApiService from '../../services/api';

export default function ContactRequests() {
  const [status, setStatus] = useState('rejected');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ApiService.adminListContactRequests(status);
      setRows(res.requests || []);
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to load requests', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (id) => {
    try {
      await ApiService.adminAcceptContactRequest(id);
      setSnackbar({ open: true, message: 'Request accepted and emailed to requester', severity: 'success' });
      load();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to accept', severity: 'error' });
    }
  };

  const handleReject = async (id) => {
    try {
      await ApiService.adminRejectContactRequest(id, 'Not permissible');
      setSnackbar({ open: true, message: 'Request rejected', severity: 'success' });
      load();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to reject', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Contact Requests
      </Typography>
      <Toolbar sx={{ gap: 2 }}>
        <FormControl size="small">
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
      </Toolbar>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Requester</TableCell>
                <TableCell>Target User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Typography variant="body2">{r.requester?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.requester?.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.targetUser?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.targetUser?.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={r.status === 'accepted' ? 'success' : r.status === 'rejected' ? 'error' : 'warning'} />
                  </TableCell>
                  <TableCell>{r.reason || '-'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="contained" sx={{ mr: 1 }} onClick={() => handleAccept(r.id)} disabled={loading}>Accept</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleReject(r.id)} disabled={loading}>Reject</Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">No contact requests found for {status} status</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}